import React, { useState } from "react";
import styles from "./RoomManagement.module.css";
import api from "../../api/axois";
import { compressImages } from "../../utils/imageUtils";
import { parseLatLng, isValidLatLng } from "../../utils/coordinates";

const initialForm = {
  title: "",
  description: "",
  price_per_night: "",
  capacity: "",
  number_of_rooms: 1,
  images: [],
  latitude: "",
  longitude: "",
  breakfast_enabled: false,
  breakfast_price: "",
  airport_conveyance_enabled: false,
  airport_conveyance_price: "",
  airport_pickup_price: "",
  airport_dropoff_price: "",
  airport_both_price: "",
  // Amenity flags
  king_size_bed: false,
  private_bathroom: false,
  smart_tv: false,
  wifi: false,
  air_conditioning: false,
};

const RoomTypeCreateModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [coordsInput, setCoordsInput] = useState("");
  const [coordsError, setCoordsError] = useState("");

  if (!open) return null;

  const handleParseCoords = () => {
    const parsed = parseLatLng(coordsInput);
    if (!parsed) {
      setCoordsError("Couldn't find coordinates in that. Paste a Google Maps link or \"lat, lng\".");
      return;
    }
    setForm((f) => ({ ...f, latitude: parsed.lat, longitude: parsed.lng }));
    setCoordsError("");
    setCoordsInput("");
  };

  const handleChange = async (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === "file") {
      if (files && files.length > 0) {
        try {
          setCompressing(true);
          const compressedFiles = await compressImages(files, 1200, 800, 0.8);
          setForm((f) => ({ ...f, images: compressedFiles }));
        } catch (error) {
          console.error("Error compressing images:", error);
          // Fallback to original files if compression fails
          setForm((f) => ({ ...f, images: Array.from(files) }));
        } finally {
          setCompressing(false);
        }
      } else {
        setForm((f) => ({ ...f, images: [] }));
      }
    } else if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "images") {
        value.forEach((file) => data.append("images[]", file));
      } else if (typeof value === "boolean") {
        data.append(key, value ? 1 : 0);
      } else {
        data.append(key, value);
      }
    });
    try {
      await api.post("/room-types", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(initialForm);
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || err.response?.data?.error || "Failed to create room type"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay} style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div className={styles.modal} style={{ maxWidth: 800, margin: 'auto' }} role="dialog" aria-modal="true" aria-labelledby="create-roomtype-heading">
        <div className={styles.modalStickyTop}>
          <h2 id="create-roomtype-heading">Create New Room Type</h2>
        </div>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Basics</h3>
            <label>
              Title
              <input name="title" value={form.title} onChange={handleChange} required />
            </label>
            <label>
              Description
              <textarea name="description" value={form.description} onChange={handleChange} required />
            </label>
            <label>
              Price per Night
              <input name="price_per_night" type="number" min="0" value={form.price_per_night} onChange={handleChange} required />
            </label>
            <label>
              Capacity
              <input name="capacity" type="number" min="1" value={form.capacity} onChange={handleChange} required />
            </label>
            <label>
              Number of Rooms
              <input name="number_of_rooms" type="number" min="1" value={form.number_of_rooms} onChange={handleChange} required />
            </label>
            <label>
              Images
              <input name="images" type="file" accept="image/*" multiple onChange={handleChange} required disabled={compressing} />
            </label>
            {compressing && <div className={styles.compressingNote}>Compressing images…</div>}
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Location</h3>
            <div className={styles.coordsRow}>
              <div>
                <label>
                  Latitude
                  <input
                    name="latitude"
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    value={form.latitude}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div>
                <label>
                  Longitude
                  <input
                    name="longitude"
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    value={form.longitude}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </div>
            <div className={styles.coordsHelper}>
              <input
                type="text"
                placeholder="Paste a Google Maps link or &quot;lat, lng&quot;"
                value={coordsInput}
                onChange={(e) => { setCoordsInput(e.target.value); setCoordsError(""); }}
                aria-label="Paste coordinates or Google Maps link"
              />
              <button type="button" className={styles.parseBtn} onClick={handleParseCoords} disabled={!coordsInput.trim()}>
                Fill in
              </button>
            </div>
            {coordsError && <p className={`${styles.coordsHint} ${styles.coordsHintError}`}>{coordsError}</p>}
            {form.latitude !== "" && form.longitude !== "" && isValidLatLng(Number(form.latitude), Number(form.longitude)) && (
              <a
                className={styles.mapLink}
                href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                View on Google Maps →
              </a>
            )}
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Add-on Pricing</h3>
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="breakfast_enabled"
                checked={form.breakfast_enabled}
                onChange={handleChange}
                id="breakfast_enabled"
              />
              <label htmlFor="breakfast_enabled">Breakfast Option Available</label>
            </div>
            {form.breakfast_enabled && (
              <div className={styles.optionPriceField}>
                <label htmlFor="breakfast_price">Breakfast Price (₦ per person per night)</label>
                <input
                  name="breakfast_price"
                  id="breakfast_price"
                  type="number"
                  min="0"
                  value={form.breakfast_price}
                  onChange={handleChange}
                  required={form.breakfast_enabled}
                />
              </div>
            )}
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="airport_conveyance_enabled"
                checked={form.airport_conveyance_enabled}
                onChange={handleChange}
                id="airport_conveyance_enabled"
              />
              <label htmlFor="airport_conveyance_enabled">Airport Conveyance Option Available</label>
            </div>
            {form.airport_conveyance_enabled && (
              <>
                <div className={styles.optionPriceField}>
                  <label htmlFor="airport_pickup_price">Airport Pickup Price (₦)</label>
                  <input
                    name="airport_pickup_price"
                    id="airport_pickup_price"
                    type="number"
                    min="0"
                    value={form.airport_pickup_price}
                    onChange={handleChange}
                    required={form.airport_conveyance_enabled}
                  />
                </div>
                <div className={styles.optionPriceField}>
                  <label htmlFor="airport_dropoff_price">Airport Drop-off Price (₦)</label>
                  <input
                    name="airport_dropoff_price"
                    id="airport_dropoff_price"
                    type="number"
                    min="0"
                    value={form.airport_dropoff_price}
                    onChange={handleChange}
                    required={form.airport_conveyance_enabled}
                  />
                </div>
                <div className={styles.optionPriceField}>
                  <label htmlFor="airport_both_price">Airport Both (Pickup & Drop-off) Price (₦)</label>
                  <input
                    name="airport_both_price"
                    id="airport_both_price"
                    type="number"
                    min="0"
                    value={form.airport_both_price}
                    onChange={handleChange}
                    required={form.airport_conveyance_enabled}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Amenities</h3>
            <div className={styles.amenitiesGrid} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
              <div className={styles.optionToggle}>
                <input
                  type="checkbox"
                  name="king_size_bed"
                  checked={form.king_size_bed}
                  onChange={handleChange}
                  id="king_size_bed"
                />
                <label htmlFor="king_size_bed">King-size bed</label>
              </div>
              <div className={styles.optionToggle}>
                <input
                  type="checkbox"
                  name="wifi"
                  checked={form.wifi}
                  onChange={handleChange}
                  id="wifi"
                />
                <label htmlFor="wifi">Wifi</label>
              </div>
              <div className={styles.optionToggle}>
                <input
                  type="checkbox"
                  name="private_bathroom"
                  checked={form.private_bathroom}
                  onChange={handleChange}
                  id="private_bathroom"
                />
                <label htmlFor="private_bathroom">Private bathroom</label>
              </div>
              <div className={styles.optionToggle}>
                <input
                  type="checkbox"
                  name="smart_tv"
                  checked={form.smart_tv}
                  onChange={handleChange}
                  id="smart_tv"
                />
                <label htmlFor="smart_tv">Smart TV</label>
              </div>
              <div className={styles.optionToggle}>
                <input
                  type="checkbox"
                  name="air_conditioning"
                  checked={form.air_conditioning}
                  onChange={handleChange}
                  id="air_conditioning"
                />
                <label htmlFor="air_conditioning">Air conditioning</label>
              </div>
            </div>
          </div>
          {error && <div className={styles.error} role="alert">{error}</div>}
          <div className={styles.modalStickyBottom}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className={styles.createButton} disabled={loading || compressing}>{loading ? "Creating..." : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoomTypeCreateModal;
